import React from 'react';
import { Tooltip } from '@mui/material';
import { type GradingStatus, type SimilarityAndOriginalityStatus } from '../api';
import { type UrgencyInfo } from './formatters.types';

// --- Urgency Formatting ---

/**
 * Urgency score thresholds for determining exclamation mark count.
 * Lower scores = more urgent = more exclamation marks.
 *
 * Based on urgency calculation in the backend (calculates_urgency.rb):
 * - Score formula: (grad_component * 10,000) + grading_component
 * - grad_component = days_until_graduation + 500 (if <= 35 days) or 1000 (if > 35 days)
 * - grading_component = days_until_deadline + 500 (if <= 7 days) or 1000 (if > 7 days)
 *
 * Threshold meanings (examples assume grading deadline not urgent unless stated):
 * - Score <= 5,050,000: 4 marks (within 5 days or less of graduation)
 *   Example: 4 days to grad, deadline not urgent → (4+500)*10000 + 1000 = 5,041,000
 * - Score 5,050,001 - 5,350,000: 3 marks (graduation within ~35 days)
 *   Example: 34 days to grad, deadline not urgent → (34+500)*10000 + 1000 = 5,341,000
 * - Score 5,350,001 - 10,000,500: 2 marks (at or past grading deadline)
 *   Example: graduation far, deadline at 0 days → 1000*10000 + (0+500) = 10,000,500
 * - Score > 10,000,500: 1 mark (approaching grading deadline, 1-7 days)
 *   Example: graduation far, deadline at 1 day → 1000*10000 + (1+500) = 10,000,501
 */
export const URGENCY_THRESHOLDS = {
    MOST_URGENT: 5_050_000,
    VERY_URGENT: 5_350_000,
    URGENT: 10_000_500,
} as const;

/**
 * Urgency level labels shown in tooltips.
 */
export const URGENCY_LABELS = {
    MOST_URGENT: 'Within 5 Days of Graduation',
    VERY_URGENT: 'Within ~35 Days of Graduation',
    URGENT: 'At Or Past Grading Deadline',
    APPROACHING_DEADLINE: 'Approaching Grading Deadline',
} as const;

/**
 * Special tooltip labels for resubmissions and capstone projects.
 */
export const SPECIAL_URGENCY_LABELS = {
    RESUBMITTED: 'Resubmitted',
    CAPSTONE: 'Capstone',
} as const;

/**
 * Determines the urgency level, mark count, and label for a given urgency score.
 * Returns null if the score is null (not urgent).
 */
export function getUrgencyInfo(urgencyScore: number | null): UrgencyInfo | null {
    if (urgencyScore == null) return null;

    if (urgencyScore <= URGENCY_THRESHOLDS.MOST_URGENT) {
        return { level: 'mostUrgent', markCount: 4, label: URGENCY_LABELS.MOST_URGENT };
    }
    if (urgencyScore <= URGENCY_THRESHOLDS.VERY_URGENT) {
        return { level: 'veryUrgent', markCount: 3, label: URGENCY_LABELS.VERY_URGENT };
    }
    if (urgencyScore <= URGENCY_THRESHOLDS.URGENT) {
        return { level: 'urgent', markCount: 2, label: URGENCY_LABELS.URGENT };
    }
    return { level: 'approachingDeadline', markCount: 1, label: URGENCY_LABELS.APPROACHING_DEADLINE };
}

/**
 * Converts an urgency score to exclamation mark indicators with a tooltip.
 * Lower scores = more urgent = more exclamation marks.
 *
 * Special tooltip overrides:
 * - Resubmissions at 2 marks (!!) show "Resubmitted" instead of default label
 * - Capstone/capstone presentation projects at 1 mark (!) show "Capstone" instead of default label
 *
 * @param urgencyScore - The urgency score from the backend, or null if not urgent
 * @param isResubmission - Whether this is a resubmission
 * @param isCapstoneOrCapstonePresentation - Whether this is a capstone project or capstone presentation
 * @returns A React node with styled exclamation marks and tooltip, or null if not urgent
 */
export function formatUrgencyMarks(
    urgencyScore: number | null,
    isResubmission?: boolean,
    isCapstoneOrCapstonePresentation?: boolean,
): React.ReactNode {
    const info = getUrgencyInfo(urgencyScore);
    if (!info) return null;

    // Override tooltip for special cases
    let tooltipLabel = info.label;
    if (isResubmission && info.markCount === 2) {
        tooltipLabel = SPECIAL_URGENCY_LABELS.RESUBMITTED;
    } else if (isCapstoneOrCapstonePresentation && info.markCount === 1) {
        tooltipLabel = SPECIAL_URGENCY_LABELS.CAPSTONE;
    }

    return (
        <Tooltip title={tooltipLabel} enterDelay={0} enterNextDelay={0}>
            <span style={{ color: '#d32f2f', fontWeight: 'bold', cursor: 'help' }}>{'!'.repeat(info.markCount)}</span>
        </Tooltip>
    );
}

// --- Grading Status Formatting ---

/**
 * Human-readable labels for grading statuses.
 */
export const GRADING_STATUS_LABELS: Record<GradingStatus, string> = {
    graded: 'Graded',
    waived: 'Waived',
    provisional: 'Provisional',
    awaiting_similarity_and_originality: 'Awaiting S&O',
    submitted: 'Submitted',
    '-': '-',
};

/**
 * Formats a grading status value to a human-readable label.
 *
 * @param status - The grading status from the backend
 * @returns Human-readable status label
 */
export function formatGradingStatus(status: GradingStatus | string): string {
    return GRADING_STATUS_LABELS[status as GradingStatus] ?? status;
}

// --- S&O Status Formatting ---

/**
 * Human-readable labels for S&O statuses.
 */
export const SIMILARITY_AND_ORIGINALITY_STATUS_LABELS: Record<SimilarityAndOriginalityStatus, string> = {
    not_checked: 'Not Checked',
    check_failed: 'Check Failed',
    no_issues: 'No Issues',
    issues_detected_needs_review: 'Issues - Needs Review',
    issues_detected_no_action_needed: 'Issues - No Action Needed',
    issues_detected_hearing_required: 'Issues - Hearing Required',
    issues_detected_allow_resubmission: 'Issues - Allow Resubmission',
    issues_detected_resubmission_required: 'Issues - Resubmission Required',
    issues_detected_no_resubmission_allowed: 'Issues - No Resubmission',
};

/**
 * Formats an S&O status value to a human-readable label.
 *
 * @param status - The S&O status from the backend
 * @returns Human-readable status label
 */
export function formatSimilarityAndOriginalityStatus(status: SimilarityAndOriginalityStatus): string {
    return SIMILARITY_AND_ORIGINALITY_STATUS_LABELS[status] ?? status;
}
